/* ===========================================================================
   Pet portrait pendant - product page
   Vanilla JS, no dependencies. One boot per section instance.

   - Gallery, quantity, sticky bar and add-to-cart follow the Snoozie harness
     page (same cart-drawer contract: post with `sections`, hand the response to
     <cart-drawer>.renderContents()).
   - Add to cart posts multipart FormData so the photo files travel with the
     line item. Shopify stores each one as a URL on the order.
   - The live preview is a canvas: the photo is turned into an engraving-style
     line drawing (pencil-sketch dodge + Sobel edges), tinted for the chosen
     metal and drawn onto a rendered pendant with the pet's name underneath.
   - No money maths: every price string comes from Liquid's money filter.
   =========================================================================== */
(function () {
  'use strict';

  var MAX_BYTES = 20 * 1024 * 1024;

  var TONES = {
    silver: { hi: '#F1F2F4', mid: '#D3D6DA', lo: '#B4B8BE', rim: '#9FA4AB', ink: [26, 26, 28], chain: ['#E9EBEE', '#9CA1A8'] },
    gold:   { hi: '#F6E3A6', mid: '#DDBB6A', lo: '#BE9443', rim: '#A57E35', ink: [40, 30, 14], chain: ['#F6DE9A', '#B08736'] },
    rose:   { hi: '#F6DCD1', mid: '#E0B4A2', lo: '#C28F7C', rim: '#A77662', ink: [46, 28, 24], chain: ['#F2CDBF', '#B37D6B'] }
  };

  /* ---------------- image processing ---------------- */

  function boxBlur(src, w, h, r) {
    if (r < 1) return src.slice();
    var tmp = new Float32Array(w * h), out = new Float32Array(w * h);
    var x, y, i, acc, span = r * 2 + 1;
    for (y = 0; y < h; y++) {
      acc = 0;
      for (i = -r; i <= r; i++) acc += src[y * w + Math.min(w - 1, Math.max(0, i))];
      for (x = 0; x < w; x++) {
        tmp[y * w + x] = acc / span;
        acc += src[y * w + Math.min(w - 1, x + r + 1)] - src[y * w + Math.max(0, x - r)];
      }
    }
    for (x = 0; x < w; x++) {
      acc = 0;
      for (i = -r; i <= r; i++) acc += tmp[Math.min(h - 1, Math.max(0, i)) * w + x];
      for (y = 0; y < h; y++) {
        out[y * w + x] = acc / span;
        acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x];
      }
    }
    return out;
  }

  function percentile(arr, q) {
    var hist = new Uint32Array(256), n = arr.length, i;
    for (i = 0; i < n; i++) hist[Math.max(0, Math.min(255, (arr[i] * 255) | 0))]++;
    var target = n * q, run = 0;
    for (i = 0; i < 256; i++) { run += hist[i]; if (run >= target) return i / 255; }
    return 1;
  }

  function smooth(a, b, x) {
    if (a === b) return x < a ? 0 : 1;
    var t = Math.max(0, Math.min(1, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
  }

  function gauss(src, w, h, sigma) {
    var r = Math.max(1, Math.round(Math.sqrt(12 * sigma * sigma / 3 + 1) / 2 - 0.5));
    return boxBlur(boxBlur(boxBlur(src, w, h, r), w, h, r), w, h, r);
  }

  function pct(values, q) {
    var a = Float32Array.from(values).sort();
    return a[Math.max(0, Math.min(a.length - 1, Math.floor(a.length * q)))];
  }

  /* Line-art engraving. A contrast-normalised difference of Gaussians finds the
     fur, eye and outline edges (normalising by local brightness is what keeps
     white and golden fur from vanishing), and the threshold is a percentile of
     the centre of the photo, so every picture comes out at the same line density
     however bright or dark it was taken. The darkest few percent (eyes, nose)
     fill solid, like a real engraving. Returns a canvas: colour = ink, alpha = line. */
  function engrave(img, crop, detail, ink) {
    var S = 420;
    var c = document.createElement('canvas');
    c.width = S; c.height = S;
    var g = c.getContext('2d', { willReadFrequently: true });
    g.fillStyle = '#fff'; g.fillRect(0, 0, S, S);
    g.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, S, S);
    var px = g.getImageData(0, 0, S, S).data, n = S * S, i, x, y;

    var L = new Float32Array(n);
    for (i = 0; i < n; i++) L[i] = (px[i * 4] * 0.299 + px[i * 4 + 1] * 0.587 + px[i * 4 + 2] * 0.114) / 255;
    var lo = percentile(L, 0.01), hi = percentile(L, 0.99), span = Math.max(0.05, hi - lo);
    for (i = 0; i < n; i++) L[i] = Math.max(0, Math.min(1, (L[i] - lo) / span));

    var g1 = gauss(L, S, S, 1.3), g2 = gauss(L, S, S, 3.0);
    var D = new Float32Array(n), R = new Float32Array(n), centreD = [], centreG = [];
    for (y = 0; y < S; y++) {
      for (x = 0; x < S; x++) {
        i = y * S + x;
        D[i] = (g1[i] - g2[i]) / (g2[i] + 0.12);
        var dx = (x - S / 2) / (S * 0.47), dy = (y - S / 2) / (S * 0.5);
        R[i] = Math.sqrt(dx * dx + dy * dy);
        if (R[i] < 0.8 && ((x + y) & 1)) { centreD.push(D[i]); centreG.push(g1[i]); }
      }
    }
    var q = 0.13;                                   /* tuned at real pendant size: denser turns to a blob */
    var t = Math.min(-0.001, pct(centreD, q));
    var dk = pct(centreG, 0.03);

    var A = new Float32Array(n);
    for (i = 0; i < n; i++) {
      var a0 = 1 - smooth(t, t * 0.7, D[i]);
      A[i] = Math.max(a0, 1 - smooth(dk, dk + 0.03, g1[i]));
    }
    /* despeckle: a pixel survives only if its neighbourhood agrees. Lone fur dots
       vanish and strokes close up into clean engraved lines. */
    var Ab = boxBlur(A, S, S, 1);
    var out = g.createImageData(S, S), o = out.data;
    for (i = 0; i < n; i++) {
      var a = smooth(0.3, 0.55, Ab[i]);
      a *= 1 - smooth(0.82, 0.98, R[i]);
      var j = i * 4;
      o[j] = ink[0]; o[j + 1] = ink[1]; o[j + 2] = ink[2];
      o[j + 3] = Math.round(Math.max(0, Math.min(1, a)) * 255);
    }
    g.clearRect(0, 0, S, S);
    g.putImageData(out, 0, 0);
    return c;
  }

  /* ---------------- AI portrait (runs in the shopper's browser) ----------------
     1. u2netp cuts the pet out of the background.
     2. The head is framed from that silhouette: close-ups keep the whole pet,
        full-body shots crop to the mass at the top of the silhouette.
     3. An "informative drawings" line-art model redraws the crop as pen lines.
     4. Lines are thickened (3x3 max), masked to the pet and faded at the neck so
        the result reads like a hand-engraved head portrait at pendant size.
     Models are theme assets; onnxruntime-web comes from jsDelivr. Nothing is sent
     anywhere and there is no per-preview cost. If any of it fails the page falls
     back to the classic engraving filter. */
  var AI = { seg: null, line: null, loading: null, failed: false };

  function loadScript(src) {
    return new Promise(function (res, rej) {
      if (window.ort) return res();
      var s = document.createElement('script');
      s.src = src; s.async = true; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
  }

  function aiLoad(cfg) {
    if (AI.loading) return AI.loading;
    if (!cfg || !cfg.ort) { AI.failed = true; return (AI.loading = Promise.reject(new Error('no ai config'))); }
    AI.loading = loadScript(cfg.ort).then(function () {
      window.ort.env.wasm.wasmPaths = cfg.wasm;
      window.ort.env.wasm.numThreads = 1;
      var opt = { executionProviders: ['wasm'], graphOptimizationLevel: 'all' };
      return Promise.all([
        window.ort.InferenceSession.create(cfg.seg, opt),
        window.ort.InferenceSession.create(cfg.line, opt)
      ]);
    }).then(function (ss) { AI.seg = ss[0]; AI.line = ss[1]; })
      .catch(function (e) { AI.failed = true; throw e; });
    return AI.loading;
  }

  function pixels(img, sx, sy, sw, sh, W, H) {
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var g = c.getContext('2d', { willReadFrequently: true });
    g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);
    g.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
    return g.getImageData(0, 0, W, H).data;
  }

  function aiPortrait(img) {
    var ort = window.ort;
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    var S = 320, n = S * S, i;
    var px = pixels(img, 0, 0, iw, ih, S, S);
    var mean = [0.485, 0.456, 0.406], sd = [0.229, 0.224, 0.225];
    var x = new Float32Array(3 * n);
    for (i = 0; i < n; i++) for (var ch = 0; ch < 3; ch++) x[ch * n + i] = (px[i * 4 + ch] / 255 - mean[ch]) / sd[ch];
    var feeds = {}; feeds[AI.seg.inputNames[0]] = new ort.Tensor('float32', x, [1, 3, S, S]);
    return AI.seg.run(feeds).then(function (out) {
      var m = out[AI.seg.outputNames[0]].data, lo = Infinity, hi = -Infinity, k;
      for (k = 0; k < n; k++) { if (m[k] < lo) lo = m[k]; if (m[k] > hi) hi = m[k]; }
      var M = new Float32Array(n), x0 = S, x1 = -1, y0 = S, y1 = -1;
      for (k = 0; k < n; k++) {
        M[k] = (m[k] - lo) / (hi - lo + 1e-6);
        if (M[k] > 0.5) { var xx = k % S, yy = (k / S) | 0; if (xx < x0) x0 = xx; if (xx > x1) x1 = xx; if (yy < y0) y0 = yy; if (yy > y1) y1 = yy; }
      }
      /* head box, in source-image pixels */
      var fx = iw / S, fy = ih / S, box;
      if (x1 < 0) {
        var s0 = Math.min(iw, ih); box = { x: (iw - s0) / 2, y: (ih - s0) / 2, s: s0 };
      } else {
        var bw = (x1 - x0) * fx, bh = (y1 - y0) * fy, side, cx, cy;
        var fill = ((x1 - x0) * (y1 - y0)) / n;
        if (fill > 0.45) {
          side = Math.min(Math.max(bw, bh) * 1.05, iw, ih);
          cx = (x0 + x1) / 2 * fx; cy = (y0 + y1) / 2 * fy;
        } else {
          var sum = 0, cnt = 0, lim = y0 + 0.45 * (y1 - y0);
          for (k = 0; k < n; k++) if (M[k] > 0.5 && ((k / S) | 0) < lim) { sum += k % S; cnt++; }
          cx = (cnt ? sum / cnt : (x0 + x1) / 2) * fx;
          side = Math.min(0.62 * Math.max(bw, bh), iw, ih);
          cy = y0 * fy + side * 0.46;
        }
        box = { x: Math.max(0, Math.min(iw - side, cx - side / 2)), y: Math.max(0, Math.min(ih - side, cy - side / 2)), s: side };
      }
      var L = 512, nl = L * L;
      var p2 = pixels(img, box.x, box.y, box.s, box.s, L, L);
      var xin = new Float32Array(3 * nl);
      for (i = 0; i < nl; i++) { xin[i] = p2[i * 4] / 255; xin[nl + i] = p2[i * 4 + 1] / 255; xin[2 * nl + i] = p2[i * 4 + 2] / 255; }
      var f2 = {}; f2[AI.line.inputNames[0]] = new ort.Tensor('float32', xin, [1, 3, L, L]);
      return AI.line.run(f2).then(function (o2) {
        var y = o2[AI.line.outputNames[0]].data;
        var ink = new Float32Array(nl), tmp = new Float32Array(nl), A = new Float32Array(nl), u, v;
        for (i = 0; i < nl; i++) ink[i] = Math.max(0, Math.min(1, (0.9 - y[i]) / 0.4));
        /* thicken strokes: 3x3 max, separable */
        for (v = 0; v < L; v++) for (u = 0; u < L; u++) {
          var a = ink[v * L + u];
          if (u > 0 && ink[v * L + u - 1] > a) a = ink[v * L + u - 1];
          if (u < L - 1 && ink[v * L + u + 1] > a) a = ink[v * L + u + 1];
          tmp[v * L + u] = a;
        }
        for (v = 0; v < L; v++) for (u = 0; u < L; u++) {
          var b = tmp[v * L + u];
          if (v > 0 && tmp[(v - 1) * L + u] > b) b = tmp[(v - 1) * L + u];
          if (v < L - 1 && tmp[(v + 1) * L + u] > b) b = tmp[(v + 1) * L + u];
          ink[v * L + u] = b;
        }
        /* pet mask for the crop, softened */
        var mk = new Float32Array(nl);
        for (v = 0; v < L; v++) for (u = 0; u < L; u++) {
          var sx = (box.x + (u + 0.5) / L * box.s) / fx, sy = (box.y + (v + 0.5) / L * box.s) / fy;
          var mi = Math.max(0, Math.min(S - 1, sy | 0)) * S + Math.max(0, Math.min(S - 1, sx | 0));
          mk[v * L + u] = M[mi];
        }
        mk = boxBlur(boxBlur(mk, L, L, 3), L, L, 3);
        for (v = 0; v < L; v++) {
          var fade = 1 - smooth(0.8, 1.0, v / L);
          for (u = 0; u < L; u++) {
            var q = v * L + u;
            A[q] = Math.min(1, ink[q] * 1.15) * smooth(0.25, 0.6, mk[q]) * fade;
          }
        }
        return A;
      });
    });
  }

  function tintAlpha(A, ink) {
    var L = 512, c = document.createElement('canvas'); c.width = L; c.height = L;
    var g = c.getContext('2d'), d = g.createImageData(L, L), o = d.data;
    for (var i = 0; i < A.length; i++) {
      var j = i * 4; o[j] = ink[0]; o[j + 1] = ink[1]; o[j + 2] = ink[2]; o[j + 3] = Math.round(A[i] * 255);
    }
    g.putImageData(d, 0, 0);
    return c;
  }

  /* ---------------- scene ---------------- */

  function layout(count) {
    var W = 900;
    if (count <= 1) return { r: 200, pts: [[W / 2, 590]] };
    if (count === 2) return { r: 150, pts: [[W / 2 - 128, 610], [W / 2 + 128, 610]] };
    if (count === 3) return { r: 122, pts: [[W / 2 - 215, 620], [W / 2, 648], [W / 2 + 215, 620]] };
    return { r: 102, pts: [[W / 2 - 285, 612], [W / 2 - 96, 648], [W / 2 + 96, 648], [W / 2 + 285, 612]] };
  }

  function chainLinks(g, pts, tone) {
    for (var i = 0; i < pts.length - 1; i++) {
      var a = pts[i], b = pts[i + 1];
      var ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
      var mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
      g.save();
      g.translate(mx, my);
      g.rotate(ang);
      g.beginPath();
      if (i % 2) { g.ellipse(0, 0, 9, 3.2, 0, 0, Math.PI * 2); }
      else { g.ellipse(0, 0, 9, 6.4, 0, 0, Math.PI * 2); }
      g.lineWidth = 2.6;
      g.strokeStyle = tone.chain[1];
      g.stroke();
      g.lineWidth = 1.2;
      g.strokeStyle = tone.chain[0];
      g.stroke();
      g.restore();
    }
  }

  function curvePts(x0, y0, cx, cy, x1, y1, step) {
    var len = Math.hypot(cx - x0, cy - y0) + Math.hypot(x1 - cx, y1 - cy);
    var n = Math.max(4, Math.round(len / step)), out = [];
    for (var i = 0; i <= n; i++) {
      var t = i / n, u = 1 - t;
      out.push([u * u * x0 + 2 * u * t * cx + t * t * x1, u * u * y0 + 2 * u * t * cy + t * t * y1]);
    }
    return out;
  }

  /* Flat satin disc, like the real stainless pendant: a soft linear sheen, a thin
     bevelled edge and a small shadow - not a glossy dome. */
  function drawDisc(g, x, y, r, tone) {
    g.save();
    g.shadowColor = 'rgba(40,30,20,.16)';
    g.shadowBlur = r * 0.08;
    g.shadowOffsetY = r * 0.03;
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2);
    g.fillStyle = tone.lo; g.fill();
    g.restore();
    var grd = g.createLinearGradient(x - r, y - r, x + r, y + r);
    grd.addColorStop(0, tone.hi);
    grd.addColorStop(0.5, tone.mid);
    grd.addColorStop(1, tone.hi);
    g.beginPath(); g.arc(x, y, r - r * 0.035, 0, Math.PI * 2);
    g.fillStyle = grd; g.fill();
    g.beginPath(); g.arc(x, y, r - r * 0.035, 0, Math.PI * 2);
    g.lineWidth = 1.2; g.strokeStyle = 'rgba(255,255,255,.75)'; g.stroke();
    g.beginPath(); g.arc(x, y, r - 0.6, 0, Math.PI * 2);
    g.lineWidth = 1.2; g.strokeStyle = tone.rim; g.stroke();
    /* jump ring */
    g.beginPath(); g.ellipse(x, y - r - 13, 9, 15, 0, 0, Math.PI * 2);
    g.lineWidth = 5; g.strokeStyle = tone.rim; g.stroke();
    g.lineWidth = 2.2; g.strokeStyle = tone.hi; g.stroke();
  }

  function drawPlaceholder(g, x, y, r, tone) {
    var c = 'rgba(' + tone.ink.join(',') + ',.38)';
    g.save();
    g.fillStyle = c;
    var s = r / 100;
    g.translate(x, y - r * 0.12);
    [[-30, -18, 11, 14], [-10, -32, 11, 14], [12, -32, 11, 14], [32, -18, 11, 14]].forEach(function (t) {
      g.beginPath(); g.ellipse(t[0] * s, t[1] * s, t[2] * s, t[3] * s, 0, 0, Math.PI * 2); g.fill();
    });
    g.beginPath(); g.ellipse(0, 14 * s, 30 * s, 24 * s, 0, 0, Math.PI * 2); g.fill();
    g.restore();
  }

  /* ---------------- boot ---------------- */

  function boot(root) {
    if (!root || root.__pndBooted) return;
    root.__pndBooted = true;

    var dataEl = root.querySelector('[data-pnd-data]');
    if (!dataEl) return;
    var D;
    try { D = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var VARIANTS = D.variants || [];
    var form = root.querySelector('[data-pnd-form]');
    var idInput = root.querySelector('[data-pnd-id]');
    var qtyField = root.querySelector('[data-pnd-qty-field]');
    var atc = root.querySelector('[data-pnd-atc]');
    var errBox = root.querySelector('[data-pnd-formerr]');
    var petErr = root.querySelector('[data-pnd-err]');
    var stage = root.querySelector('[data-pnd-stage]');
    var slides = [].slice.call(root.querySelectorAll('[data-pnd-slide]'));
    var thumbs = [].slice.call(root.querySelectorAll('[data-pnd-thumb]'));
    var canvas = root.querySelector('[data-pnd-canvas]');
    var zoomIn = root.querySelector('[data-pnd-zoom]');
    var detailIn = root.querySelector('[data-pnd-detail]');
    var tools = root.querySelector('[data-pnd-tools]');
    var busy = root.querySelector('[data-pnd-busy]');
    var metalProp = root.querySelector('[data-pnd-metal-prop]');
    var pets = [].slice.call(root.querySelectorAll('[data-pnd-pet]'));

    var state = {
      metal: D.initial.metal,
      count: parseInt(D.initial.count, 10) || 1,
      countVal: D.initial.count,
      variant: null,
      qty: 1,
      slide: 0,
      active: 0,
      tier: 0,
      bundleN: (D.tiers && D.tiers[0]) ? D.tiers[0].qty : 1
    };

    /* how many photo + name slots are open: pendants on one chain, or separate necklaces in a bundle */
    function slots() { return D.hasBundle ? state.bundleN : state.count; }
    /* a necklace marked "same pet as #1" borrows pet 1's photo and name */
    function isSame(i) {
      if (!i) return false;
      var cb = pets[i] && pets[i].querySelector('[data-pnd-same]');
      return !!(cb && cb.checked);
    }
    function sourceOf(i) { return isSame(i) ? 0 : i; }
    function paintViews() {
      pets.forEach(function (el, i) {
        var ps = petState[sourceOf(i)], own = petState[i];
        el.classList.toggle('is-loading', !!(own.loading || own.aiPending));
        var vb = el.querySelector('[data-pnd-view]');
        if (!vb) return;
        var busyNow = !!(ps && (ps.loading || ps.aiPending));
        vb.hidden = !(ps && (ps.img || ps.loading) && !ps.isSample);
        vb.classList.toggle('is-loading', busyNow);
        var lab = vb.querySelector('span');
        if (lab) {
          if (!vb.getAttribute('data-label')) vb.setAttribute('data-label', lab.textContent);
          lab.textContent = busyNow ? (D.labels.drawingShort || 'Drawing…') : vb.getAttribute('data-label');
        }
      });
      var pm = document.querySelector('[data-pnd-pvm]');
      if (pm) {
        var ap = petState[sourceOf(state.active)];
        pm.classList.toggle('is-loading', !!(ap && (ap.loading || ap.aiPending)));
      }
    }
    function paintSame() {
      paintViews();
      pets.forEach(function (el, i) {
        if (!i) return;
        var same = isSame(i);
        el.classList.toggle('is-same', same);
        el.querySelectorAll('[data-pnd-file],[data-pnd-name]').forEach(function (inp) {
          inp.disabled = same || i >= slots();
        });
      });
    }

    /* per-pet: decoded image, crop offset, cached engraving */
    var petState = pets.map(function () { return { img: null, heic: false, ox: 0, oy: 0, zoom: 100, detail: 100, cache: null, cacheKey: '', metal: D.initial.metal }; });

    function toneFor(metal) {
      var m = (metal || '').toLowerCase();
      if (m.indexOf('rose') > -1) return 'rose';
      if (m.indexOf('gold') > -1) return 'gold';
      return 'silver';
    }

    /* ---------------- variants ---------------- */
    function findVariant() {
      var exact = null, fallback = null;
      for (var i = 0; i < VARIANTS.length; i++) {
        var v = VARIANTS[i];
        var mOk = !D.hasMetal || v.metal === state.metal;
        var cOk = !D.hasCount || v.count === state.countVal;
        var bOk = true;
        if (mOk && cOk && bOk) { if (v.available) return v; if (!exact) exact = v; }
        if (mOk && !fallback) fallback = v;
      }
      return exact || fallback || VARIANTS[0] || null;
    }

    function paintPrice() {
      var v = state.variant; if (!v) return;
      var now = root.querySelector('[data-pnd-now]');
      var was = root.querySelector('[data-pnd-was]');
      var off = root.querySelector('[data-pnd-off]');
      var tt = D.hasBundle && v.tiers && v.tiers[state.tier];
      var nowTxt = tt ? tt.total : v.price;
      if (now) now.textContent = nowTxt;
      if (tt) {
        if (was) { was.hidden = tt.value === tt.total; was.textContent = tt.value; }
        if (off) off.hidden = true;
      } else {
        if (was) { was.hidden = !v.compare; if (v.compare) was.textContent = v.compare; }
        if (off) { off.hidden = !v.pct; if (v.pct) off.textContent = '-' + v.pct + '%'; }
      }
      var ap = root.querySelector('[data-pnd-atc-price]');
      if (ap) ap.textContent = nowTxt;
      var sp = root.querySelector('[data-pnd-sticky-price]');
      if (sp) sp.textContent = nowTxt;
      if (atc) {
        atc.disabled = !v.available;
        var lab = root.querySelector('[data-pnd-atc-label]');
        if (lab) lab.textContent = v.available ? D.labels.atc : D.labels.soldOut;
        ['[data-pnd-atc-dot]', '[data-pnd-atc-price]'].forEach(function (s) {
          var el = root.querySelector(s); if (el) el.hidden = !v.available;
        });
      }
    }

    function apply(opts) {
      state.variant = findVariant();
      if (state.variant && idInput) idInput.value = state.variant.id;
      if (metalProp) metalProp.value = state.metal;
      root.querySelectorAll('[data-pnd-metal]').forEach(function (b) {
        var on = b.getAttribute('data-pnd-metal') === state.metal;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-pnd-count]').forEach(function (b) {
        var on = b.getAttribute('data-pnd-count') === state.countVal;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      var ml = root.querySelector('[data-pnd-metal-label]'); if (ml) ml.textContent = state.metal;
      var cl = root.querySelector('[data-pnd-count-label]'); if (cl) cl.textContent = state.countVal;
      root.querySelectorAll('[data-pnd-tier]').forEach(function (b) {
        var k = +b.getAttribute('data-pnd-tier'), on = k === state.tier;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        var tt = state.variant && state.variant.tiers && state.variant.tiers[k];
        if (!tt) return;
        var nw = b.querySelector('[data-pnd-tier-now]'), sb = b.querySelector('[data-pnd-tier-sub]');
        if (nw) nw.textContent = tt.total;
        if (sb) sb.textContent = (b.getAttribute('data-sub') || '').replace('{n}', b.getAttribute('data-qty')).replace('{value}', tt.value);
      });
      pets.forEach(function (el, i) {
        var show = i < slots();
        el.hidden = !show;
        el.querySelectorAll('input').forEach(function (inp) { inp.disabled = !show; });
      });
      paintSame();
      /* The page always lands on the first product photo. The gallery only follows
         a variant's image when the shopper picks a metal themselves, and not once
         they've uploaded a photo - slide 1 is then their personalised pendant. */
      var hasUpload = petState.some(function (p) { return p.img && !p.isSample; });
      if (opts && opts.jump && !hasUpload && state.variant && state.variant.mediaId) {
        for (var s = 0; s < slides.length; s++) {
          if (slides[s].getAttribute('data-media-id') === String(state.variant.mediaId)) { showSlide(s); break; }
        }
      }
      paintPrice();
      render();
    }

    /* ---------------- gallery ---------------- */
    function markActive(i) {
      state.slide = i;
      slides.forEach(function (s, n) {
        var vid = s.querySelector('video');
        if (vid && n !== i) vid.pause();
      });
      thumbs.forEach(function (t, n) { t.classList.toggle('is-on', n === i); });
      root.querySelectorAll('[data-pnd-dots] i').forEach(function (d, n) { d.classList.toggle('is-on', n === i); });
      var prev = root.querySelector('[data-pnd-prev]'), next = root.querySelector('[data-pnd-next]');
      if (prev) prev.hidden = slides.length < 2 || i === 0;
      if (next) next.hidden = slides.length < 2 || i >= slides.length - 1;
    }
    function showSlide(i) {
      if (!stage || !slides.length) return;
      i = Math.max(0, Math.min(slides.length - 1, i));
      stage.scrollTo({ left: i * stage.clientWidth, behavior: 'smooth' });
      markActive(i);
    }
    if (stage) {
      var tick = 0;
      stage.addEventListener('scroll', function () {
        cancelAnimationFrame(tick);
        tick = requestAnimationFrame(function () {
          markActive(Math.round(stage.scrollLeft / (stage.clientWidth || 1)));
        });
      }, { passive: true });
      markActive(0);
    }

    /* ---------------- real pendant photo ---------------- */
    /* The preview is drawn on the store's own blank-pendant photo. Gold and rose
       versions are made from the silver shot once, by mapping each metal pixel's
       brightness onto that metal's ramp; the pure-white background is left alone. */
    var BASE = { img: null, tinted: {}, cx: D.baseGeo[0], cy: D.baseGeo[1], r: D.baseGeo[2] };
    var RAMPS = {
      gold: [[92, 66, 22], [214, 178, 96], [255, 243, 205]],
      rose: [[104, 60, 48], [220, 168, 148], [255, 236, 228]]
    };
    function tint(img, ramp) {
      var c = document.createElement('canvas');
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      var g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(img, 0, 0);
      var d = g.getImageData(0, 0, c.width, c.height), px = d.data;
      for (var i = 0; i < px.length; i += 4) {
        var l = (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114) / 255;
        var m = 1 - smooth(0.965, 0.995, l);             /* 0 on the white backdrop */
        if (m <= 0) continue;
        var a = l < 0.5 ? ramp[0] : ramp[1], b = l < 0.5 ? ramp[1] : ramp[2], t = l < 0.5 ? l * 2 : (l - 0.5) * 2;
        for (var k = 0; k < 3; k++) {
          var col = a[k] + (b[k] - a[k]) * t;
          px[i + k] = Math.round(px[i + k] + (col - px[i + k]) * m);
        }
      }
      g.putImageData(d, 0, 0);
      return c;
    }
    function baseFor(toneKey) {
      if (!BASE.img) return null;
      if (toneKey === 'silver') return BASE.img;
      if (!BASE.tinted[toneKey]) {
        try { BASE.tinted[toneKey] = tint(BASE.img, RAMPS[toneKey]); }
        catch (e) { BASE.tinted[toneKey] = BASE.img; }
      }
      return BASE.tinted[toneKey];
    }
    if (D.baseImg && canvas) {
      var bi = new Image();
      bi.crossOrigin = 'anonymous';
      bi.onload = function () { BASE.img = bi; render(); };
      bi.src = D.baseImg;
    }

    /* ---------------- preview ---------------- */
    var rq = 0;
    function render() {
      if (!canvas) return;
      cancelAnimationFrame(rq);
      rq = requestAnimationFrame(draw);
    }

    function cropFor(ps) {
      var img = ps.img, iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
      var side = Math.min(iw, ih) / (ps.zoom / 100);
      var sx = (iw - side) / 2 - ps.ox * side, sy = (ih - side) / 2 - ps.oy * side;
      sx = Math.max(0, Math.min(iw - side, sx));
      sy = Math.max(0, Math.min(ih - side, sy));
      return { sx: sx, sy: sy, sw: side, sh: side };
    }

    /* Name set on an arc along the bottom of the disc, like the real engraving. */
    function arcText(g, text, cx, cy, radius, fs) {
      g.font = '400 ' + fs + 'px "Times New Roman", Times, Georgia, serif';
      var spacing = fs * 0.02, widths = [], total = 0, k;
      for (k = 0; k < text.length; k++) { widths.push(g.measureText(text[k]).width); total += widths[k] + (k ? spacing : 0); }
      var span = total / radius, ang = Math.PI / 2 + span / 2;
      g.textAlign = 'center'; g.textBaseline = 'alphabetic';
      for (k = 0; k < text.length; k++) {
        var half = widths[k] / 2 / radius;
        ang -= half;
        g.save();
        g.translate(cx + radius * Math.cos(ang), cy + radius * Math.sin(ang));
        g.rotate(ang - Math.PI / 2);
        g.fillText(text[k], 0, 0);
        g.restore();
        ang -= half + spacing / radius;
      }
    }

    function drawPortrait(g, i, x, y, r, tone, toneKey) {
      var pet = pets[i], ps = petState[i];
      var nameEl = pet && pet.querySelector('[data-pnd-name]');
      var name = nameEl ? nameEl.value.trim() : '';
      if (!name && i === 0 && (!ps.img || ps.isSample) && D.sampleName) name = D.sampleName;
      /* same proportions as the product photo: head in the upper two-thirds, name below */
      var portraitY = name ? y - r * 0.2 : y - r * 0.02;
      var ps2 = r * (name ? 1.56 : 1.8);
      g.save();
      g.beginPath(); g.arc(x, y, r * 0.93, 0, Math.PI * 2); g.clip();
      if (ps.img && ps.aiPending && !ps.ai && !ps.aiFailed) {
        /* the AI drawing takes a few seconds: say so on the pendant itself */
        g.fillStyle = 'rgba(' + tone.ink.join(',') + ',.55)';
        g.textAlign = 'center'; g.textBaseline = 'middle';
        g.font = '500 ' + Math.round(r * 0.16) + 'px "Times New Roman", Times, serif';
        g.fillText(D.labels.drawing || 'Drawing your pet…', x, portraitY + r * 0.05);
      } else if (ps.img) {
        var key = [ps.ai ? 'ai' : 'dog', ps.zoom, ps.detail, ps.ox.toFixed(3), ps.oy.toFixed(3), toneKey].join('|');
        if (ps.cacheKey !== key) {
          try { ps.cache = ps.ai ? tintAlpha(ps.ai, tone.ink) : engrave(ps.img, cropFor(ps), ps.detail, tone.ink); ps.cacheKey = key; }
          catch (e) { ps.cache = null; }
        }
        if (ps.cache) {
          var pz = ps.ai ? ps2 * 0.9 : ps2;
          g.globalCompositeOperation = 'multiply';
          g.drawImage(ps.cache, x - pz / 2, portraitY - pz / 2, pz, pz);
          g.globalCompositeOperation = 'source-over';
        }
      } else {
        drawPlaceholder(g, x, portraitY, r * 0.8, tone);
      }
      g.restore();
      if (name) {
        /* straight, centred, like the engraved "Bella" on the product photo;
           long names shrink to fit inside the disc instead of running into the portrait */
        g.save();
        g.fillStyle = 'rgb(' + tone.ink.join(',') + ')';
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        var fs = r * 0.28, maxW = r * 1.3;
        g.font = '400 ' + fs + 'px "Times New Roman", Times, Georgia, serif';
        var w = g.measureText(name).width;
        if (w > maxW) { fs = fs * maxW / w; g.font = '400 ' + fs + 'px "Times New Roman", Times, Georgia, serif'; }
        /* a gentle smile curve, following the bottom of the disc: letters sit on
           the lower edge of a wide circle whose centre is above the pendant */
        var R = r * 1.9, base = y + r * 0.68 + fs * 0.34;
        g.font = '400 ' + fs + 'px "Times New Roman", Times, Georgia, serif';
        arcText(g, name, x, base - R, R, fs);
        g.restore();
      }
    }

    /* The scene is the store's own product photo with the engraving removed: the
       full chain and pendant at exactly the size shoppers see in the gallery. Extra
       pendants are copies of that disc hung beside the first. */
    function drawOnPhoto(g, W, H, toneKey, tone) {
      var base = baseFor(toneKey);
      var fx = (BASE.cx / 100) * W, fy = (BASE.cy / 100) * H, fr = (BASE.r / 100) * W;
      g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);
      g.drawImage(base, 0, 0, W, H);
      var n = D.hasBundle ? 1 : state.count, gap = fr * 1.55;
      var bw = base.width || base.naturalWidth, sc = bw / W;
      /* disc + jump ring crop, in base-image pixels */
      var cxp = fx * sc, cyp = fy * sc, rp = fr * sc;
      var crop = { x: cxp - rp * 1.08, y: cyp - rp * 1.5, w: rp * 2.16, h: rp * 2.6 };
      var xs = [fx];
      if (n === 2) xs = [fx - gap / 2, fx + gap / 2];
      if (n === 3) xs = [fx - gap, fx, fx + gap];
      if (n === 4) xs = [fx - gap * 1.5, fx - gap / 2, fx + gap / 2, fx + gap * 1.5];
      if (n > 1) {
        /* blank the original disc, then hang n copies */
        g.fillStyle = '#fff';
        g.fillRect(crop.x / sc, crop.y / sc + fr * 0.35, crop.w / sc, crop.h / sc - fr * 0.35);
        for (var k = 0; k < n; k++) {
          g.save();
          g.globalCompositeOperation = 'multiply';
          g.drawImage(base, crop.x, crop.y, crop.w, crop.h, xs[k] - crop.w / sc / 2, crop.y / sc, crop.w / sc, crop.h / sc);
          g.restore();
        }
      }
      for (var i = 0; i < n; i++) drawPortrait(g, D.hasBundle ? sourceOf(state.active) : i, xs[i], fy, fr, tone, toneKey);
      mirrorToGallery();
    }

    /* Once a photo is in, the first gallery image shows the personalised pendant too. */
    var galCanvas = null;
    function mirrorToGallery() {
      if (galCanvas) { galCanvas.remove(); galCanvas = null; }
      return;
      var any = petState.some(function (p, i) { return i < slots() && p.img && !p.isSample; });
      if (!any || !slides.length) { if (galCanvas) galCanvas.hidden = true; return; }
      if (!galCanvas) {
        galCanvas = document.createElement('canvas');
        galCanvas.className = 'pnd-gal-cv';
        galCanvas.width = canvas.width; galCanvas.height = canvas.height;
        slides[0].appendChild(galCanvas);
      }
      galCanvas.hidden = false;
      galCanvas.getContext('2d').drawImage(canvas, 0, 0);
    }

    function draw() {
      var g = canvas.getContext('2d');
      var W = canvas.width, H = canvas.height;
      var toneKey = toneFor(D.hasBundle ? (petState[state.active].metal || state.metal) : state.metal), tone = TONES[toneKey];
      g.clearRect(0, 0, W, H);
      if (BASE.img) { drawOnPhoto(g, W, H, toneKey, tone); return; }
      var bg = g.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#FBF9F6'); bg.addColorStop(1, '#EFEAE3');
      g.fillStyle = bg; g.fillRect(0, 0, W, H);

      var L = layout(state.count);
      var tipY = L.pts[0][1] - L.r - 24;
      var hubX = W / 2, hubY = state.count > 1 ? Math.min.apply(null, L.pts.map(function (p) { return p[1]; })) - L.r - 30 : tipY;

      /* chain: two strands from the top corners to the hub */
      chainLinks(g, curvePts(150, -20, 250, hubY * 0.72, hubX, hubY, 11), tone);
      chainLinks(g, curvePts(W - 150, -20, W - 250, hubY * 0.72, hubX, hubY, 11), tone);
      /* for several pendants, short drops from the hub to each bail */
      if (state.count > 1) {
        L.pts.forEach(function (p) {
          chainLinks(g, curvePts(hubX, hubY, (hubX + p[0]) / 2, hubY + 6, p[0], p[1] - L.r - 24, 10), tone);
        });
      }

      var inkCss = 'rgb(' + tone.ink.join(',') + ')';
      for (var i = 0; i < state.count; i++) {
        var x = L.pts[i][0], y = L.pts[i][1], r = L.r;
        drawDisc(g, x, y, r, tone);
        var pet = pets[i], ps = petState[i];
        var nameEl = pet && pet.querySelector('[data-pnd-name]');
        var name = nameEl ? nameEl.value.trim() : '';
        if (!name && i === 0 && (!ps.img || ps.isSample) && D.sampleName) name = D.sampleName;
        var portraitY = name ? y - r * 0.13 : y;
        var ps2 = r * (name ? 1.32 : 1.55);

        g.save();
        g.beginPath(); g.arc(x, y, r * 0.92, 0, Math.PI * 2); g.clip();
        if (ps.img) {
          var key = [ps.zoom, ps.detail, ps.ox.toFixed(3), ps.oy.toFixed(3), toneKey].join('|');
          if (ps.cacheKey !== key) {
            try { ps.cache = engrave(ps.img, cropFor(ps), ps.detail, tone.ink); ps.cacheKey = key; }
            catch (e) { ps.cache = null; }
          }
          if (ps.cache) {
            g.drawImage(ps.cache, x - ps2 / 2, portraitY - ps2 / 2, ps2, ps2);
          }
        } else {
          drawPlaceholder(g, x, portraitY, r, tone);
        }
        g.restore();

        if (name) {
          g.save();
          g.fillStyle = inkCss;
          g.globalAlpha = 0.92;
          g.textAlign = 'center';
          g.textBaseline = 'middle';
          var fs = Math.round(r * (name.length > 7 ? 0.17 : 0.21));
          g.font = '400 ' + fs + 'px "Times New Roman", Times, Georgia, serif';
          g.fillText(name, x, y + r * 0.64);
          g.restore();
        }
      }
    }

    var pvm = root.querySelector('[data-pnd-pvm]');
    if (pvm && pvm.parentNode !== document.body) { pvm.style.cssText = root.getAttribute('style') || ''; pvm.classList.add('pnd-portal'); document.body.appendChild(pvm); }
    function openPreview(i) {
      if (!pvm) return;
      setActive(i);
      var h = pvm.querySelector('[data-pnd-pvm-h]');
      if (h) h.textContent = D.hasBundle && slots() > 1 ? (D.labels.pvSlot || 'Necklace #{n}').replace('{n}', i + 1) : (D.labels.pvHeading || h.textContent);
      render();
      pvm.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    }
    function closePreview() { if (!pvm) return; pvm.hidden = true; document.documentElement.style.overflow = ''; }
    if (pvm) {
      pvm.addEventListener('click', function (e) { if (e.target.closest('[data-pnd-pvm-close]')) closePreview(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !pvm.hidden) closePreview(); });
    }

    function setActive(i) {
      if (!petState[i]) return;
      var changed = state.active !== i;
      state.active = i;
      pets.forEach(function (el, n) { el.classList.toggle('is-active', D.hasBundle && n === i); });
      if (changed) render();
      var ps = petState[i];
      if (zoomIn) zoomIn.value = ps.zoom;
      if (detailIn) detailIn.value = ps.detail;
      if (tools) tools.hidden = true;
    }

    /* ---------------- photo input ---------------- */
    function isHeic(file) {
      return /hei[cf]$/i.test(file.name || '') || /hei[cf]/i.test(file.type || '');
    }

    function loadFile(i, file) {
      var pet = pets[i], ps = petState[i];
      var empty = pet.querySelector('.pnd-drop-empty'), full = pet.querySelector('.pnd-drop-full');
      var thumb = pet.querySelector('[data-pnd-thumbimg]'), fname = pet.querySelector('[data-pnd-fname]');
      if (petErr) petErr.hidden = true;
      if (!file) return;
      if (!/^image\//.test(file.type || '') && !isHeic(file)) { fail(D.labels.photoType); return; }
      if (file.size > MAX_BYTES) { fail(D.labels.photoSize); return; }

      if (empty) empty.hidden = true;
      if (full) full.hidden = false;
      if (fname) fname.textContent = file.name || 'photo';
      pet.classList.add('has-photo');

      var url = URL.createObjectURL(file);
      var img = new Image();
      if (busy) busy.hidden = false;
      ps.loading = true; paintViews();
      img.onload = function () {
        if (busy) busy.hidden = true;
        ps.loading = false;
        if (thumb) thumb.src = url;
        ps.img = img; ps.isSample = false; ps.heic = false; ps.ox = 0; ps.oy = 0; ps.cacheKey = '';
        ps.ai = null; ps.aiFailed = false; ps.aiPending = !!D.ai;
        var token = ps.token = (ps.token || 0) + 1;
        setActive(i);
        render();
        paintViews();
        if (D.ai) {
          aiLoad(D.ai).then(function () { return aiPortrait(img); })
            .then(function (A) { if (ps.token !== token) return; ps.ai = A; ps.aiPending = false; ps.cacheKey = ''; render(); paintViews(); })
            .catch(function () { if (ps.token !== token) return; ps.aiFailed = true; ps.aiPending = false; ps.cacheKey = ''; render(); paintViews(); });
        }
      };
      img.onerror = function () {
        if (busy) busy.hidden = true;
        ps.loading = false; ps.aiPending = false; paintViews();
        ps.img = null;
        if (isHeic(file)) {
          ps.heic = true;
          if (thumb) thumb.removeAttribute('src');
          if (petErr) { petErr.hidden = false; petErr.classList.add('is-note'); petErr.textContent = D.labels.heic; }
          render();
        } else {
          fail(D.labels.photoType);
        }
      };
      img.src = url;

      function fail(msg) {
        var inp = pet.querySelector('[data-pnd-file]');
        if (inp) inp.value = '';
        if (empty) empty.hidden = false;
        if (full) full.hidden = true;
        pet.classList.remove('has-photo');
        ps.img = null;
        if (petErr) { petErr.hidden = false; petErr.classList.remove('is-note'); petErr.textContent = msg; }
        render();
      }
    }

    pets.forEach(function (pet, i) {
      var inp = pet.querySelector('[data-pnd-file]');
      var drop = pet.querySelector('[data-pnd-drop]');
      var name = pet.querySelector('[data-pnd-name]');
      var chars = pet.querySelector('[data-pnd-count-chars]');
      if (inp) inp.addEventListener('change', function () { loadFile(i, inp.files && inp.files[0]); });
      if (drop && D.ai) ['pointerdown', 'focusin', 'dragenter'].forEach(function (ev) {
        drop.addEventListener(ev, function () { aiLoad(D.ai).catch(function () {}); }, { once: true });
      });
      if (drop && inp) {
        ['dragenter', 'dragover'].forEach(function (ev) {
          drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('is-over'); });
        });
        ['dragleave', 'drop'].forEach(function (ev) {
          drop.addEventListener(ev, function () { drop.classList.remove('is-over'); });
        });
        drop.addEventListener('drop', function (e) {
          e.preventDefault();
          var f = e.dataTransfer && e.dataTransfer.files;
          if (!f || !f.length) return;
          try { inp.files = f; } catch (err) { /* old Safari: preview still works */ }
          loadFile(i, f[0]);
        });
      }
      if (name) {
        name.addEventListener('input', function () {
          var v = name.value;
          if (v && v.charAt(0) !== v.charAt(0).toUpperCase()) {
            var pos = name.selectionStart;
            name.value = v.charAt(0).toUpperCase() + v.slice(1);
            try { name.setSelectionRange(pos, pos); } catch (e) {}
          }
          if (chars) chars.textContent = name.value.length + '/' + D.nameMax;
          render();
        });
        name.addEventListener('focus', function () { setActive(i); });
      }
      pet.addEventListener('click', function () { setActive(i); });
    });

    /* ---------------- options ---------------- */
    root.addEventListener('click', function (e) {
      var t = e.target;
      var m = t.closest('[data-pnd-metal]');
      if (m) { state.metal = m.getAttribute('data-pnd-metal'); apply({ jump: true }); return; }
      var vw = t.closest('[data-pnd-view]');
      if (vw) { openPreview(pets.indexOf(vw.closest('[data-pnd-pet]'))); return; }
      var smb = t.closest('[data-pnd-slot-metal]');
      if (smb) {
        var pi = pets.indexOf(smb.closest('[data-pnd-pet]')), mv = smb.getAttribute('data-pnd-slot-metal');
        if (pi > -1) {
          petState[pi].metal = mv; petState[pi].cacheKey = '';
          pets[pi].querySelectorAll('[data-pnd-slot-metal]').forEach(function (b) {
            var on = b === smb; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', on ? 'true' : 'false');
          });
          var ml2 = pets[pi].querySelector('[data-pnd-slot-metal-label]'); if (ml2) ml2.textContent = mv;
          state.active = pi; render();
        }
        return;
      }
      var bt = t.closest('[data-pnd-tier]');
      if (bt) {
        state.tier = +bt.getAttribute('data-pnd-tier');
        state.bundleN = Math.max(1, Math.min(4, parseInt(bt.getAttribute('data-qty'), 10) || 1));
        if (state.active >= slots()) setActive(0);
        apply();
        return;
      }
      var sm = t.closest('[data-pnd-same]');
      if (sm) { setTimeout(function () { paintSame(); render(); }, 0); }
      var c = t.closest('[data-pnd-count]');
      if (c) {
        state.countVal = c.getAttribute('data-pnd-count');
        state.count = Math.max(1, Math.min(4, parseInt(state.countVal, 10) || 1));
        if (state.active >= state.count) setActive(0);
        apply(); return;
      }
      var th = t.closest('[data-pnd-thumb]');
      if (th) { showSlide(thumbs.indexOf(th)); return; }
      if (t.closest('[data-pnd-prev]')) { showSlide(state.slide - 1); return; }
      if (t.closest('[data-pnd-next]')) { showSlide(state.slide + 1); return; }
      var q = t.closest('[data-pnd-qty]');
      if (q) {
        state.qty = Math.max(1, Math.min(20, state.qty + (q.getAttribute('data-pnd-qty') === 'up' ? 1 : -1)));
        paintQty(); return;
      }
      if (t.closest('[data-pnd-sticky-btn]')) { addToCart(); return; }
    });

    var qtyInput = root.querySelector('[data-pnd-qty-input]');
    function paintQty() {
      if (qtyInput) qtyInput.value = state.qty;
      if (qtyField) qtyField.value = state.qty;
      var down = root.querySelector('[data-pnd-qty="down"]');
      if (down) down.disabled = state.qty <= 1;
    }
    if (qtyInput) qtyInput.addEventListener('change', function () {
      state.qty = Math.max(1, Math.min(20, parseInt(qtyInput.value, 10) || 1));
      paintQty();
    });

    /* ---------------- arrival dates ---------------- */
    var arrive = root.querySelector('[data-pnd-arrive]');
    if (arrive) {
      var fmt = function (days) {
        var d = new Date(); d.setDate(d.getDate() + days);
        try { return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }); }
        catch (e) { return d.toDateString(); }
      };
      arrive.textContent = arrive.textContent
        .replace('{min}', fmt(+arrive.getAttribute('data-days-min') || 7))
        .replace('{max}', fmt(+arrive.getAttribute('data-days-max') || 12));
    }

    /* ---------------- add to cart ---------------- */
    function showFormError(msg) {
      if (!errBox) return;
      errBox.hidden = !msg; errBox.textContent = msg || '';
    }

    function firstMissing() {
      for (var i = 0; i < slots(); i++) {
        if (isSame(i)) continue;
        var inp = pets[i].querySelector('[data-pnd-file]');
        if (!inp || !inp.files || !inp.files.length) return i;
      }
      return -1;
    }

    function addToCart() {
      showFormError('');
      if (D.photoRequired && pets.length) {
        var miss = firstMissing();
        if (miss > -1) {
          var pet = pets[miss];
          if (petErr) { petErr.hidden = false; petErr.classList.remove('is-note'); petErr.textContent = D.labels.photoMissing; }
          pet.classList.add('is-missing');
          setTimeout(function () { pet.classList.remove('is-missing'); }, 1600);
          pet.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      if (!state.variant || !state.variant.available) return;

      var btns = [atc, root.querySelector('[data-pnd-sticky-btn]')].filter(Boolean);
      btns.forEach(function (b) { b.classList.add('is-busy'); b.disabled = true; });
      if (D.hasBundle) return addBundle(btns);

      var fd = new FormData(form);
      /* drop empty photo / name fields so the order only lists what was given */
      var drop = [];
      fd.forEach(function (v, k) {
        if (v instanceof File && !v.size) drop.push(k);
        else if (typeof v === 'string' && k.indexOf('properties[') === 0 && !v.trim()) drop.push(k);
      });
      drop.forEach(function (k) { fd.delete(k); });
      fd.set('quantity', String(state.qty));
      fd.append('sections', 'cart-drawer,cart-icon-bubble');
      fd.append('sections_url', window.location.pathname);

      /* one preview image per necklace in a bundle, so the fulfiller sees each */
      var withPreview = new Promise(function (resolve) {
        if (!D.attachPreview || !canvas || !canvas.toBlob) return resolve();
        var list = [];
        for (var pi = 0; pi < slots(); pi++) if (petState[pi] && petState[pi].img && !petState[pi].isSample) list.push(pi);
        if (!list.length) return resolve();
        var keep = state.active, base = D.previewProp || '_Preview';
        (function next(k) {
          if (k >= list.length) { state.active = keep; render(); return resolve(); }
          var idx = list[k];
          if (D.hasBundle) state.active = idx;
          try { draw(); } catch (e) {}
          try {
            canvas.toBlob(function (blob) {
              if (blob) fd.append('properties[' + base + (D.hasBundle ? ' ' + (idx + 1) : '') + ']', blob, 'engraving-preview-' + (idx + 1) + '.jpg');
              next(D.hasBundle ? k + 1 : list.length);
            }, 'image/jpeg', 0.86);
          } catch (e) { next(list.length); }
        })(0);
      });

      withPreview.then(function () {
        return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd
        });
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status && res.status !== 200) {
            showFormError(res.description || res.message || D.labels.addError);
            return;
          }
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(res);
          else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          btns.forEach(function (b) { b.classList.add('is-done'); });
          setTimeout(function () { btns.forEach(function (b) { b.classList.remove('is-done'); }); }, 1400);
        })
        .catch(function () { showFormError(D.labels.addError); })
        .finally(function () {
          btns.forEach(function (b) { b.classList.remove('is-busy'); });
          paintPrice();
          var sb = root.querySelector('[data-pnd-sticky-btn]'); if (sb) sb.disabled = false;
        });
    }

    /* Offer tiers: every necklace is its own cart line (qty 1) carrying its own
       photo, name and preview, so the store's Buy X get Y discount counts them and
       the fulfiller gets one clear line per necklace. Lines are added one after
       another; only the last asks for the drawer sections. */
    function previewBlob(src) {
      return new Promise(function (resolve) {
        if (!D.attachPreview || !canvas || !canvas.toBlob || !petState[src].img) return resolve(null);
        var keep = state.active;
        state.active = src;
        try { draw(); } catch (e) {}
        try {
          canvas.toBlob(function (b) { state.active = keep; resolve(b); }, 'image/jpeg', 0.86);
        } catch (e) { state.active = keep; resolve(null); }
      });
    }

    function variantForMetal(metal) {
      if (!D.hasMetal) return state.variant;
      for (var k = 0; k < VARIANTS.length; k++) {
        var v = VARIANTS[k];
        if (v.metal === metal && (!D.hasCount || v.count === state.countVal)) return v;
      }
      return state.variant;
    }

    function addBundle(btns) {
      var n = slots(), jobs = [], blobs = {};
      /* offer tiers on a product with no personalisation: one line, n units, so the
         store's Buy X get Y discount applies */
      if (!pets.length) {
        return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: state.variant.id, quantity: n }], sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
        }).then(function (r) { return r.json(); }).then(function (res) {
          if (res.status && res.status !== 200) throw new Error(res.description || res.message || D.labels.addError);
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(res);
          else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }).catch(function (e) { showFormError((e && e.message) || D.labels.addError); })
          .finally(function () { btns.forEach(function (b) { b.classList.remove('is-busy'); }); paintPrice(); });
      }
      /* one tap = one AddToCart for the Meta pixel, however many lines it adds */
      window.__metaAtcBatch = [];
      var metalInp = root.querySelector('[data-pnd-metal-prop]');
      function fileOf(i) { var f = pets[i].querySelector('[data-pnd-file]'); return f && f.files && f.files[0]; }
      function nameOf(i) { var f = pets[i].querySelector('[data-pnd-name]'); return f ? f.value.trim() : ''; }
      var chain = Promise.resolve();
      var srcs = [];
      for (var i = 0; i < n; i++) { var s0 = sourceOf(i); if (srcs.indexOf(s0) < 0) srcs.push(s0); }
      srcs.forEach(function (sidx) {
        chain = chain.then(function () { return previewBlob(sidx).then(function (b) { blobs[sidx] = b; }); });
      });
      var last = null;
      chain = chain.then(function () {
        render();
        var seq = Promise.resolve();
        for (var j = 0; j < n; j++) (function (j) {
          seq = seq.then(function () {
            var src = sourceOf(j), fd = new FormData();
            fd.append('id', variantForMetal(petState[j].metal).id);
            fd.append('quantity', '1');
            fd.append('properties[' + D.props.necklace + ']', (j + 1) + ' of ' + n);
            if (metalInp && metalInp.value) fd.append(metalInp.name, metalInp.value);
            var f = fileOf(src); if (f) fd.append('properties[' + D.props.photo + ']', f, f.name);
            var nm = nameOf(src); if (nm) fd.append('properties[' + D.props.name + ']', nm);
            if (blobs[src]) fd.append('properties[' + (D.previewProp || '_Preview') + ']', blobs[src], 'engraving-preview-' + (j + 1) + '.jpg');
            if (j === n - 1) { fd.append('sections', 'cart-drawer,cart-icon-bubble'); fd.append('sections_url', window.location.pathname); }
            return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
              method: 'POST', headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body: fd
            }).then(function (r) { return r.json(); }).then(function (res) {
              if (res.status && res.status !== 200) throw new Error(res.description || res.message || D.labels.addError);
              last = res;
            });
          });
        })(j);
        return seq;
      });
      return chain.then(function () {
        if (window.__metaAtcFlush) window.__metaAtcFlush();
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function' && last) drawer.renderContents(last);
        else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        btns.forEach(function (b) { b.classList.add('is-done'); });
        setTimeout(function () { btns.forEach(function (b) { b.classList.remove('is-done'); }); }, 1400);
      }).catch(function (e) { if (window.__metaAtcFlush) window.__metaAtcFlush(); showFormError((e && e.message) || D.labels.addError); })
        .finally(function () {
          btns.forEach(function (b) { b.classList.remove('is-busy'); });
          paintPrice();
          var sb = root.querySelector('[data-pnd-sticky-btn]'); if (sb) sb.disabled = false;
        });
    }

    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); addToCart(); });

    /* ---------------- sticky bar ---------------- */
    var sticky = root.querySelector('[data-pnd-sticky]');
    if (sticky && atc && 'IntersectionObserver' in window) {
      sticky.classList.add('is-up');
      new IntersectionObserver(function (es) {
        es.forEach(function (e) { sticky.classList.toggle('is-up', !e.isIntersecting); });
      }, { threshold: 0.35 }).observe(atc);
      root.addEventListener('focusin', function (e) {
        if (e.target.matches('input[type="text"]')) sticky.classList.add('is-typing');
      });
      root.addEventListener('focusout', function () {
        setTimeout(function () { sticky.classList.remove('is-typing'); }, 250);
      });
    }

    /* optional sample photo before upload */
    if (D.sampleImg && canvas) {
      var s = new Image();
      s.crossOrigin = 'anonymous';
      s.onload = function () {
        if (!petState[0].img) { petState[0].img = s; petState[0].isSample = true; render(); }
      };
      s.src = D.sampleImg;
    }

    setActive(0);
    paintQty();
    apply();
  }

  /* deferred gallery photos: fill in after load (idle) or as soon as the shopper swipes */
  function hydrateGallery() {
    document.querySelectorAll('img[data-pnd-lazy]').forEach(function (im) {
      im.srcset = im.getAttribute('data-srcset'); im.removeAttribute('data-pnd-lazy');
    });
  }
  function scheduleGallery() {
    var go = function () { (window.requestIdleCallback || function (f) { setTimeout(f, 200); })(hydrateGallery, { timeout: 2500 }); };
    if (document.readyState === 'complete') setTimeout(go, 1500); else window.addEventListener('load', function () { setTimeout(go, 1500); });
    document.addEventListener('pointerdown', function h(e) {
      if (e.target.closest && e.target.closest('[data-pnd-stage],[data-pnd-thumb],[data-pnd-next],[data-pnd-prev]')) { hydrateGallery(); document.removeEventListener('pointerdown', h, true); }
    }, true);
    document.addEventListener('scroll', function sh(e) {
      if (e.target && e.target.matches && e.target.matches('[data-pnd-stage]')) { hydrateGallery(); document.removeEventListener('scroll', sh, true); }
    }, true);
  }
  scheduleGallery();
  function init() { document.querySelectorAll('[data-pnd]').forEach(boot); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
